import { useEffect, useState } from 'react';
import React from 'react';
import {
  Button,
  H2,
  Input,
  Paragraph,
  Spinner,
  Text,
  TextArea,
  XStack,
  YStack,
} from 'tamagui';
import { ListItem, Screen } from '@mrrmlab/ui';
import { useQuery } from '@tanstack/react-query';
import type { Store } from '@mrrmlab/shared-types';
import {
  useAddShoppingListItem,
  useDeleteShoppingListItem,
  useShoppingList,
  useShoppingLists,
  useTransferMissingShoppingItems,
  useUpdateShoppingList,
  useUpdateShoppingListItem,
} from '../hooks';
import { useApiClient } from '../api-client-context';

// ---------------------------------------------------------------------------
// OCR model options
// ---------------------------------------------------------------------------

export const OCR_MODELS = [
  { id: 'mlkit/de', label: 'Deutsch', sub: 'On-Device' },
  { id: 'easyocr/de', label: 'Deutsch', sub: 'Server (Backup)' },
] as const;

export type OcrModelId = typeof OCR_MODELS[number]['id'];
export const DEFAULT_OCR_MODEL: OcrModelId = 'mlkit/de';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ShoppingListScreenProps {
  listId: string;
  onBack?: () => void;
  onCameraPress?: (model: OcrModelId) => void;
  onTextImportPress?: () => void;
  onEnterShoppingMode?: () => void;
  isParsing?: boolean;
  parseError?: string | null;
  /** Slot for the ArticleCatalogSheet — rendered below the add form when open. */
  catalogSheet?: React.ReactNode;
  /** Called when the user taps "Aus Katalog". */
  onCatalogPress?: () => void;
}

export function ShoppingListScreen({ listId, onBack, onCameraPress, onTextImportPress, onEnterShoppingMode, isParsing, parseError, catalogSheet, onCatalogPress }: ShoppingListScreenProps) {
  const query = useShoppingList(listId);
  const addItem = useAddShoppingListItem(listId);
  const updateItem = useUpdateShoppingListItem(listId);
  const deleteItem = useDeleteShoppingListItem(listId);
  const updateList = useUpdateShoppingList();
  const transferMissing = useTransferMissingShoppingItems(listId);
  const [title, setTitle] = useState('');
  const [quantityText, setQuantityText] = useState('1');
  const [description, setDescription] = useState('');
  const [changingStore, setChangingStore] = useState(false);
  const [ocrModel, setOcrModel] = useState<OcrModelId>(DEFAULT_OCR_MODEL);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingListTitle, setEditingListTitle] = useState(false);
  const [editingListTitleValue, setEditingListTitleValue] = useState('');
  const [editingPlannedDate, setEditingPlannedDate] = useState(false);
  const [editingPlannedDateValue, setEditingPlannedDateValue] = useState('');
  const [transferring, setTransferring] = useState(false);

  const startEdit = (itemId: string, currentTitle: string) => {
    setEditingId(itemId);
    setEditingTitle(currentTitle);
  };

  const submitEdit = (itemId: string) => {
    const trimmed = editingTitle.trim();
    if (trimmed) {
      updateItem.mutate({ itemId, dto: { title: trimmed } });
    }
    setEditingId(null);
  };

  const startEditListTitle = (currentTitle: string) => {
    setEditingListTitleValue(currentTitle);
    setEditingListTitle(true);
  };

  const submitListTitle = () => {
    const trimmed = editingListTitleValue.trim();
    if (trimmed) {
      updateList.mutate({ id: listId, dto: { title: trimmed } });
    }
    setEditingListTitle(false);
  };

  const parsedQuantity = parseQuantity(quantityText);
  const canSubmit = title.trim().length > 0 && parsedQuantity !== null;

  const submit = () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle || parsedQuantity === null) return;
    const trimmedDescription = description.trim();
    addItem.mutate(
      {
        title: trimmedTitle,
        quantity: parsedQuantity,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      },
      {
        onSuccess: () => {
          setTitle('');
          setQuantityText('1');
          setDescription('');
        },
      },
    );
  };

  if (query.isPending) {
    return (
      <Screen>
        <Spinner />
      </Screen>
    );
  }

  if (query.isError) {
    return (
      <Screen>
        <Paragraph color="$red10">Failed to load list: {query.error.message}</Paragraph>
        {onBack ? <Button onPress={onBack}>Back</Button> : null}
      </Screen>
    );
  }

  const list = query.data;
  const sortedItems = [...list.items].sort((a, b) => {
    if (a.position !== b.position) return a.position - b.position;
    return a.createdAt.localeCompare(b.createdAt);
  });
  const missingCount = sortedItems.filter((i) => i.missing).length;

  const selectStore = (id: string | null) => {
    updateList.mutate({ id: listId, dto: { storeId: id } });
    setChangingStore(false);
  };

  const handleTransferToExisting = (targetListId: string) => {
    transferMissing.mutate(
      { targetListId },
      { onSuccess: () => setTransferring(false) },
    );
  };

  const handleTransferToNew = (newListTitle: string) => {
    const trimmed = newListTitle.trim();
    if (!trimmed) return;
    transferMissing.mutate(
      { newListTitle: trimmed },
      { onSuccess: () => setTransferring(false) },
    );
  };

  return (
    <Screen scrollable>
      <XStack alignItems="center" gap="$3">
        {onBack ? (
          <Button size="$2" chromeless onPress={onBack}>
            ← Back
          </Button>
        ) : null}
        {editingListTitle ? (
          <>
            <Input
              flex={1}
              value={editingListTitleValue}
              onChangeText={setEditingListTitleValue}
              onSubmitEditing={submitListTitle}
              fontSize="$6"
              fontWeight="700"
              autoFocus
            />
            <Button size="$2" chromeless onPress={submitListTitle}>✓</Button>
            <Button size="$2" chromeless onPress={() => setEditingListTitle(false)}>✕</Button>
          </>
        ) : (
          <>
            <H2 flex={1} onPress={() => startEditListTitle(list.title)} cursor="pointer">
              {list.title}
            </H2>
            {onCameraPress ? (
              <Button size="$2" chromeless disabled={isParsing} onPress={() => onCameraPress(ocrModel)}>
                {isParsing ? <Spinner size="small" /> : '📷'}
              </Button>
            ) : null}
            {onTextImportPress ? (
              <Button size="$2" chromeless onPress={onTextImportPress}>✍️</Button>
            ) : null}
          </>
        )}
      </XStack>

      {onCameraPress ? (
        <OcrModelSelector selected={ocrModel} onSelect={setOcrModel} disabled={isParsing ?? false} />
      ) : null}

      <ParseProgressBanner active={isParsing ?? false} />

      {parseError ? (
        <Paragraph color="$red10" fontSize="$3">
          {mapParseError(parseError)}
        </Paragraph>
      ) : null}

      <StoreBadge
        storeId={list.storeId ?? null}
        changing={changingStore}
        onStartChange={() => setChangingStore(true)}
        onCancelChange={() => setChangingStore(false)}
        onSelect={selectStore}
        busy={updateList.isPending}
      />

      <PlannedDateBadge
        plannedDate={list.plannedDate ?? null}
        editing={editingPlannedDate}
        editingValue={editingPlannedDateValue}
        onStartEdit={() => {
          setEditingPlannedDateValue(list.plannedDate ?? '');
          setEditingPlannedDate(true);
        }}
        onChange={setEditingPlannedDateValue}
        onSubmit={() => {
          const trimmed = editingPlannedDateValue.trim();
          const next = trimmed === '' ? null : trimmed;
          updateList.mutate({ id: listId, dto: { plannedDate: next } });
          setEditingPlannedDate(false);
        }}
        onCancel={() => setEditingPlannedDate(false)}
        busy={updateList.isPending}
      />

      {onEnterShoppingMode && list.items.length > 0 ? (
        <Button size="$5" onPress={onEnterShoppingMode}>
          🛒 Einkaufen-Modus
        </Button>
      ) : null}

      <YStack gap="$2">
        <XStack gap="$2" alignItems="center">
          <Input
            flex={1}
            placeholder="Artikel eingeben"
            value={title}
            onChangeText={setTitle}
            onSubmitEditing={submit}
          />
          <Input
            width={80}
            placeholder="Menge"
            value={quantityText}
            onChangeText={setQuantityText}
            keyboardType="number-pad"
            inputMode="numeric"
          />
        </XStack>
        <TextArea
          placeholder="Beschreibung (optional)"
          value={description}
          onChangeText={setDescription}
          numberOfLines={2}
        />
        <XStack gap="$2">
          <Button flex={1} disabled={addItem.isPending || !canSubmit} onPress={submit}>
            Hinzufügen
          </Button>
          {onCatalogPress && list.storeId ? (
            <Button onPress={onCatalogPress}>
              Aus Katalog
            </Button>
          ) : null}
        </XStack>
      </YStack>

      {catalogSheet}

      {missingCount > 0 ? (
        <TransferMissingPanel
          currentListId={listId}
          missingCount={missingCount}
          active={transferring}
          onActivate={() => setTransferring(true)}
          onCancel={() => setTransferring(false)}
          onTransferToExisting={handleTransferToExisting}
          onTransferToNew={handleTransferToNew}
          busy={transferMissing.isPending}
          error={transferMissing.error?.message ?? null}
        />
      ) : null}

      {sortedItems.length === 0 ? (
        <Paragraph opacity={0.7}>No items yet.</Paragraph>
      ) : (
        <YStack>
          {sortedItems.map((item) => (
            <XStack key={item.id} alignItems="center" gap="$2">
              {editingId === item.id ? (
                <>
                  <Input
                    flex={1}
                    value={editingTitle}
                    onChangeText={setEditingTitle}
                    onSubmitEditing={() => submitEdit(item.id)}
                    autoFocus
                  />
                  <Button size="$2" chromeless onPress={() => submitEdit(item.id)}>✓</Button>
                  <Button size="$2" chromeless onPress={() => setEditingId(null)}>✕</Button>
                </>
              ) : (
                <>
                  <ListItem
                    flex={1}
                    id={item.id}
                    title={item.title}
                    checked={item.checked}
                    quantity={item.quantity}
                    description={item.description}
                    missing={item.missing}
                    onToggle={(next) =>
                      updateItem.mutate({ itemId: item.id, dto: { checked: next } })
                    }
                    onMissingToggle={(next) =>
                      updateItem.mutate({ itemId: item.id, dto: { missing: next } })
                    }
                    onTitlePress={() => startEdit(item.id, item.title)}
                  />
                  <Button
                    size="$2"
                    chromeless
                    disabled={deleteItem.isPending}
                    onPress={() => deleteItem.mutate(item.id)}
                  >
                    ✕
                  </Button>
                </>
              )}
            </XStack>
          ))}
        </YStack>
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// OCR model selector
// ---------------------------------------------------------------------------

interface OcrModelSelectorProps {
  selected: OcrModelId;
  onSelect: (id: OcrModelId) => void;
  disabled: boolean;
}

function OcrModelSelector({ selected, onSelect, disabled }: OcrModelSelectorProps) {
  return (
    <XStack gap="$2" flexWrap="wrap" alignItems="center">
      <Text fontSize="$2" opacity={0.5}>Modell:</Text>
      {OCR_MODELS.map((m) => (
        <Button
          key={m.id}
          size="$2"
          disabled={disabled}
          chromeless={selected !== m.id}
          borderWidth={selected === m.id ? 1 : 0}
          onPress={() => onSelect(m.id)}
        >
          {m.label} · {m.sub}
        </Button>
      ))}
    </XStack>
  );
}

// ---------------------------------------------------------------------------
// Parse progress banner
// ---------------------------------------------------------------------------

const PARSE_STEPS = [
  'Bild wird hochgeladen…',
  'Text wird erkannt…',
  'Artikel werden verarbeitet…',
  'Noch einen Moment – längere Listen können bis zu 2 Minuten dauern…',
] as const;

// How long (ms) each step is shown before advancing to the next
const STEP_DURATIONS = [3_000, 15_000, 30_000];

function ParseProgressBanner({ active }: { active: boolean }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!active) {
      setStep(0);
      return;
    }
    if (step >= PARSE_STEPS.length - 1) return;
    const t = setTimeout(() => setStep((s) => s + 1), STEP_DURATIONS[step]);
    return () => clearTimeout(t);
  }, [active, step]);

  if (!active) return null;

  return (
    <XStack
      alignItems="center"
      gap="$3"
      paddingVertical="$3"
      paddingHorizontal="$4"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$color6"
      backgroundColor="$color2"
    >
      <Spinner size="small" />
      <YStack flex={1}>
        <Paragraph fontWeight="600" fontSize="$4">
          {PARSE_STEPS[step]}
        </Paragraph>
        <Paragraph fontSize="$2" opacity={0.6}>
          Schritt {step + 1} von {PARSE_STEPS.length}
        </Paragraph>
      </YStack>
    </XStack>
  );
}

// ---------------------------------------------------------------------------
// Error message mapping
// ---------------------------------------------------------------------------

function mapParseError(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('no text') || lower.includes('could not be recognized')) {
    return 'Kein Text erkannt – bitte ein deutlicheres Bild verwenden.';
  }
  if (lower.includes('timed out') || lower.includes('aborted') || lower.includes('not reachable')) {
    return 'Analyse dauert zu lange – bitte erneut versuchen.';
  }
  if (lower.includes('no image file') || lower.includes('bad request')) {
    return 'Ungültiges Bild – bitte ein anderes Foto wählen.';
  }
  return 'Bildanalyse fehlgeschlagen – bitte erneut versuchen.';
}

// ---------------------------------------------------------------------------
// StoreBadge
// ---------------------------------------------------------------------------

interface StoreBadgeProps {
  storeId: string | null;
  changing: boolean;
  onStartChange: () => void;
  onCancelChange: () => void;
  onSelect: (id: string | null) => void;
  busy: boolean;
}

function StoreBadge({
  storeId,
  changing,
  onStartChange,
  onCancelChange,
  onSelect,
  busy,
}: StoreBadgeProps) {
  const client = useApiClient();
  const storesQuery = useQuery<Store[]>({
    queryKey: ['stores', 'store'],
    queryFn: ({ signal }) => client.stores.list(signal),
  });

  const stores = storesQuery.data ?? [];
  const currentStore = storeId ? stores.find((s) => s.id === storeId) : undefined;

  if (changing) {
    return (
      <YStack gap="$2">
        <XStack gap="$2" flexWrap="wrap" alignItems="center">
          <Text fontSize="$2" opacity={0.7}>Store:</Text>
          <Button size="$2" onPress={() => onSelect(null)}>
            None
          </Button>
          {stores.map((s) => (
            <Button key={s.id} size="$2" onPress={() => onSelect(s.id)}>
              {s.name}
            </Button>
          ))}
          <Button size="$2" chromeless onPress={onCancelChange}>
            Cancel
          </Button>
        </XStack>
      </YStack>
    );
  }

  return (
    <XStack alignItems="center" gap="$2">
      {currentStore ? (
        <Text fontSize="$3" opacity={0.7}>📍 {currentStore.name}</Text>
      ) : (
        <Text fontSize="$3" opacity={0.4}>No store assigned</Text>
      )}
      <Button size="$2" chromeless disabled={busy} onPress={onStartChange}>
        {currentStore ? 'Change' : 'Assign store'}
      </Button>
    </XStack>
  );
}

// ---------------------------------------------------------------------------
// PlannedDateBadge
// ---------------------------------------------------------------------------

interface PlannedDateBadgeProps {
  plannedDate: string | null;
  editing: boolean;
  editingValue: string;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
}

function PlannedDateBadge({
  plannedDate,
  editing,
  editingValue,
  onStartEdit,
  onChange,
  onSubmit,
  onCancel,
  busy,
}: PlannedDateBadgeProps) {
  if (editing) {
    return (
      <XStack gap="$2" alignItems="center">
        <Text fontSize="$3" opacity={0.7}>📅</Text>
        <Input
          flex={1}
          maxWidth={180}
          placeholder="YYYY-MM-DD"
          value={editingValue}
          onChangeText={onChange}
          onSubmitEditing={onSubmit}
          inputMode="numeric"
          autoFocus
        />
        <Button size="$2" chromeless disabled={busy} onPress={onSubmit}>✓</Button>
        <Button size="$2" chromeless onPress={onCancel}>✕</Button>
      </XStack>
    );
  }

  return (
    <XStack alignItems="center" gap="$2">
      {plannedDate ? (
        <Text fontSize="$3" opacity={0.7}>📅 {plannedDate}</Text>
      ) : (
        <Text fontSize="$3" opacity={0.4}>Kein Datum geplant</Text>
      )}
      <Button size="$2" chromeless disabled={busy} onPress={onStartEdit}>
        {plannedDate ? 'Ändern' : 'Datum setzen'}
      </Button>
    </XStack>
  );
}

// ---------------------------------------------------------------------------
// TransferMissingPanel
// ---------------------------------------------------------------------------

interface TransferMissingPanelProps {
  currentListId: string;
  missingCount: number;
  active: boolean;
  onActivate: () => void;
  onCancel: () => void;
  onTransferToExisting: (targetListId: string) => void;
  onTransferToNew: (newListTitle: string) => void;
  busy: boolean;
  error: string | null;
}

function TransferMissingPanel({
  currentListId,
  missingCount,
  active,
  onActivate,
  onCancel,
  onTransferToExisting,
  onTransferToNew,
  busy,
  error,
}: TransferMissingPanelProps) {
  const listsQuery = useShoppingLists({ enabled: active });
  const [newTitle, setNewTitle] = useState('');

  if (!active) {
    return (
      <XStack alignItems="center" gap="$2">
        <Text fontSize="$3" color="$orange10">⚠ {missingCount} fehlend</Text>
        <Button size="$2" onPress={onActivate}>
          Fehlende übernehmen
        </Button>
      </XStack>
    );
  }

  const otherLists = (listsQuery.data ?? []).filter((l) => l.id !== currentListId);

  return (
    <YStack
      gap="$2"
      padding="$3"
      borderRadius="$3"
      borderWidth={1}
      borderColor="$borderColor"
      backgroundColor="$color2"
    >
      <Paragraph fontWeight="600">{missingCount} fehlende Artikel übernehmen</Paragraph>
      <Paragraph fontSize="$2" opacity={0.7}>
        Wähle eine bestehende Liste oder lege eine neue an.
      </Paragraph>

      {listsQuery.isPending ? (
        <Spinner size="small" />
      ) : otherLists.length > 0 ? (
        <XStack gap="$2" flexWrap="wrap">
          {otherLists.map((l) => (
            <Button
              key={l.id}
              size="$2"
              disabled={busy}
              onPress={() => onTransferToExisting(l.id)}
            >
              → {l.title}
            </Button>
          ))}
        </XStack>
      ) : (
        <Paragraph fontSize="$2" opacity={0.6}>Keine andere Liste vorhanden.</Paragraph>
      )}

      <XStack gap="$2" alignItems="center">
        <Input
          flex={1}
          placeholder="Neue Liste benennen…"
          value={newTitle}
          onChangeText={setNewTitle}
          onSubmitEditing={() => onTransferToNew(newTitle)}
        />
        <Button
          size="$2"
          disabled={busy || newTitle.trim().length === 0}
          onPress={() => onTransferToNew(newTitle)}
        >
          + Neu
        </Button>
      </XStack>

      {error ? (
        <Paragraph color="$red10" fontSize="$2">{error}</Paragraph>
      ) : null}

      <XStack justifyContent="flex-end">
        <Button size="$2" chromeless disabled={busy} onPress={onCancel}>
          Abbrechen
        </Button>
      </XStack>
    </YStack>
  );
}

function parseQuantity(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 1) return null;
  return n;
}
